/**
 * Cloud Operations Dashboard (WQ-126 + WQ-126a)
 *
 * Superadmin dashboard consuming all CPAL 5.5.1-5.5.5 backend endpoints.
 * Sections: Deployment, Provider & Compute, Infrastructure (IPs/LB),
 * DNS & Certificates, Cost Summary, Compliance Posture, and local-dev info banner.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  CloudIcon,
  ServerIcon,
  GlobeIcon,
  ShieldIcon,
  ActivityIcon,
  RefreshIcon,
  AlertIcon,
  CheckCircleIcon,
  DatabaseIcon,
} from '../components/common/Icons';
import * as cloudOps from '../services/cloudOpsService';
import DemoPublishCard from '../components/demos/DemoPublishCard';
import type {
  ProviderInfo,
  CloudHealth,
  IPAllocation,
  LBRule,
  ComputeHealth,
  DNSZone,
  Certificate,
  CostCurrentMonth,
  ComplianceSummary,
  DeployHealth,
  DeployStatus,
  DeployRun,
  ComputeSizeAdvisory,
} from '../services/cloudOpsService';

// =============================================================================
// Section state type
// =============================================================================

interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

function initSection<T>(): SectionState<T> {
  return { data: null, loading: true, error: null };
}

// =============================================================================
// Status badge helper
// =============================================================================

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (['running', 'ok', 'healthy', 'issued', 'active', 'pass', 'success', 'completed'].includes(s)) return 'var(--success, #22c55e)';
  if (['pending', 'initializing', 'pending_validation', 'queued'].includes(s)) return 'var(--warning, #eab308)';
  if (['stopped', 'failed', 'expired', 'error', 'impaired', 'failure', 'cancelled'].includes(s)) return 'var(--error, #ef4444)';
  if (['in_progress'].includes(s)) return 'var(--primary, #3b82f6)';
  return 'var(--text-secondary, #888)';
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        backgroundColor: statusColor(status) + '22',
        color: statusColor(status),
        border: `1px solid ${statusColor(status)}44`,
      }}
    >
      {status}
    </span>
  );
}

function InfoBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: '8px',
        backgroundColor: 'var(--info-bg, rgba(59,130,246,0.1))',
        border: '1px solid var(--info-border, rgba(59,130,246,0.3))',
        color: 'var(--text-secondary)',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      <AlertIcon size={16} />
      {message}
    </div>
  );
}

function SectionError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="alert alert-error" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <AlertIcon size={16} />
      <span style={{ flex: 1 }}>{error}</span>
      <button className="btn btn-sm" onClick={onRetry}>Retry</button>
    </div>
  );
}

function SectionLoading() {
  return (
    <div style={{ padding: '24px', textAlign: 'center' }}>
      <div className="loading-spinner" />
    </div>
  );
}

// =============================================================================
// KPI Card
// =============================================================================

function KPICard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div
      className="glass-panel"
      style={{
        flex: '1 1 0',
        minWidth: '220px',
        padding: '16px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        {icon}
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>{title}</h4>
      </div>
      {children}
    </div>
  );
}

// =============================================================================
// Time ago helper
// =============================================================================

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// =============================================================================
// Compute Size (SLM Serving) panel — WQ-332 / DM-28
// =============================================================================

function fmtMb(mb: number | null | undefined): string {
  if (mb === null || mb === undefined) return 'N/A';
  return `${mb.toLocaleString()} MB`;
}

function FitBadge({ fits }: { fits: boolean }) {
  const color = fits ? 'var(--success, #22c55e)' : 'var(--text-secondary, #888)';
  return (
    <span style={{ color, fontWeight: 600 }}>{fits ? '✓ fits' : '✗ no'}</span>
  );
}

function ComputeSizePanel({ data }: { data: ComputeSizeAdvisory }) {
  const [selected, setSelected] = useState<string>(
    data.recommended_size_for_large ||
    data.current_instance_type ||
    data.ladder[0]?.instance_type ||
    '',
  );
  const selectedMember = data.ladder.find(m => m.instance_type === selected);

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <KPICard title="Current Instance" icon={<ServerIcon size={16} />}>
          <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
            <div><strong>Type:</strong> {data.current_instance_type || 'unknown (IMDS/local)'}</div>
            <div><strong>On ladder:</strong> {data.on_ladder ? <StatusBadge status="yes" /> : <StatusBadge status="no" />}</div>
            <div><strong>Free RAM (measured):</strong> {fmtMb(data.available_ram_mb)}</div>
          </div>
        </KPICard>

        <KPICard title="Tier Headroom (now)" icon={<ActivityIcon size={16} />}>
          <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
            <div><strong>Small ({fmtMb(data.thresholds.small_min_free_ram_mb)}):</strong>{' '}
              <FitBadge fits={!!data.measured_tiers_fit.small} /></div>
            <div><strong>Large ({fmtMb(data.thresholds.large_min_free_ram_mb)}):</strong>{' '}
              <FitBadge fits={!!data.measured_tiers_fit.large} /></div>
            <div style={{ marginTop: '4px', color: 'var(--text-secondary)', fontSize: '12px' }}>
              {data.active_served_size_classes.length > 0
                ? `Active served tiers: ${data.active_served_size_classes.join(', ')}`
                : 'No active SLM deployment'}
            </div>
          </div>
        </KPICard>
      </div>

      {/* Ladder table */}
      <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Resize Ladder</h4>
      <table style={{ width: '100%', maxWidth: '640px', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
            <th style={{ textAlign: 'left', padding: '8px 4px' }}>Instance Type</th>
            <th style={{ textAlign: 'right', padding: '8px 4px' }}>Total RAM</th>
            <th style={{ textAlign: 'right', padding: '8px 4px' }}>Est. Free</th>
            <th style={{ textAlign: 'left', padding: '8px 12px' }}>Small</th>
            <th style={{ textAlign: 'left', padding: '8px 12px' }}>Large</th>
          </tr>
        </thead>
        <tbody>
          {data.ladder.map((m) => (
            <tr key={m.instance_type} style={{
              borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))',
              backgroundColor: m.is_current ? 'rgba(59,130,246,0.08)' : undefined,
            }}>
              <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>
                {m.instance_type}{m.is_current && <span style={{ color: 'var(--primary, #3b82f6)', marginLeft: '6px', fontSize: '11px' }}>(current)</span>}
              </td>
              <td style={{ padding: '8px 4px', textAlign: 'right' }}>{fmtMb(m.total_ram_mb)}</td>
              <td style={{ padding: '8px 4px', textAlign: 'right' }}>{fmtMb(m.estimated_free_ram_mb)}</td>
              <td style={{ padding: '8px 12px' }}><FitBadge fits={!!m.tiers_fit.small} /></td>
              <td style={{ padding: '8px 12px' }}><FitBadge fits={!!m.tiers_fit.large} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Size selector + dispatch link (advisory only — no in-app EC2 call) */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: '13px', fontWeight: 600 }}>Resize to:</label>
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          data-testid="resize-size-select"
          style={{
            padding: '6px 10px', borderRadius: '8px', fontSize: '13px',
            background: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color, rgba(255,255,255,0.15))',
          }}
        >
          {data.ladder.map((m) => (
            <option key={m.instance_type} value={m.instance_type}>{m.instance_type}</option>
          ))}
        </select>
        {selectedMember && (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            supports small <FitBadge fits={!!selectedMember.tiers_fit.small} />, large <FitBadge fits={!!selectedMember.tiers_fit.large} />
          </span>
        )}
        <a
          href={cloudOps.RESIZE_WORKFLOW_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary btn-sm"
          style={{ marginLeft: 'auto', textDecoration: 'none' }}
        >
          Dispatch resize in GitHub Actions &rarr;
        </a>
      </div>

      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px', lineHeight: 1.6 }}>
        Advisory only — the operator dispatches <code>resize-prod.yml</code> in GitHub Actions
        (behind the Environment gate). Est. free = total &minus; a {fmtMb(data.stack_reserved_mb)} resident-stack
        reserve; the post-grow step re-checks the real measured RAM before activating. {data.gpu_note}
      </p>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

const CloudOpsPage: React.FC = () => {
  const { authFetch } = useAuth();

  // Section states
  const [provider, setProvider] = useState<SectionState<ProviderInfo>>(initSection);
  const [health, setHealth] = useState<SectionState<CloudHealth>>(initSection);
  const [compute, setCompute] = useState<SectionState<ComputeHealth>>(initSection);
  const [ips, setIPs] = useState<SectionState<IPAllocation[]>>(initSection);
  const [lbRules, setLBRules] = useState<SectionState<LBRule[]>>(initSection);
  const [dnsZones, setDNSZones] = useState<SectionState<DNSZone[]>>(initSection);
  const [certs, setCerts] = useState<SectionState<Certificate[]>>(initSection);
  const [cost, setCost] = useState<SectionState<CostCurrentMonth>>(initSection);
  const [compliance, setCompliance] = useState<SectionState<ComplianceSummary>>(initSection);
  const [computeSize, setComputeSize] = useState<SectionState<ComputeSizeAdvisory>>(initSection);
  const [detecting, setDetecting] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Deploy states
  const [deployHealth, setDeployHealth] = useState<DeployHealth | null>(null);
  const [deployStatus, setDeployStatus] = useState<DeployStatus | null>(null);
  const [deployHistory, setDeployHistory] = useState<DeployRun[]>([]);
  const [deployLoading, setDeployLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState<string | null>(null);
  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Generic fetcher
  const fetchSection = useCallback(async <T,>(
    fetcher: (af: typeof authFetch) => Promise<T | cloudOps.CloudOpsError>,
    setter: React.Dispatch<React.SetStateAction<SectionState<T>>>,
  ) => {
    setter(prev => ({ ...prev, loading: true, error: null }));
    const result = await fetcher(authFetch);
    if (cloudOps.isCloudOpsError(result)) {
      setter({ data: null, loading: false, error: result.message });
    } else {
      setter({ data: result as T, loading: false, error: null });
    }
  }, [authFetch]);

  // Load deploy data
  const loadDeploy = useCallback(async () => {
    setDeployLoading(true);
    setDeployError(null);
    const [healthRes, statusRes, historyRes] = await Promise.all([
      cloudOps.getDeployHealth(authFetch),
      cloudOps.getDeployStatus(authFetch),
      cloudOps.getDeployHistory(authFetch, 5),
    ]);
    if (!cloudOps.isCloudOpsError(healthRes)) setDeployHealth(healthRes as DeployHealth);
    if (!cloudOps.isCloudOpsError(statusRes)) setDeployStatus(statusRes as DeployStatus | null);
    if (!cloudOps.isCloudOpsError(historyRes)) {
      // Handle both array (normal) and dict (graceful fallback) responses
      setDeployHistory(Array.isArray(historyRes) ? historyRes : (historyRes as Record<string, unknown>)?.runs as DeployRun[] ?? []);
    }
    setDeployLoading(false);
  }, [authFetch]);

  // Load all data
  const loadAll = useCallback(() => {
    fetchSection(cloudOps.getProviderInfo, setProvider);
    fetchSection(cloudOps.getCloudHealth, setHealth);
    fetchSection(cloudOps.getComputeHealth, setCompute);
    fetchSection(cloudOps.getIPAllocations, setIPs);
    fetchSection(cloudOps.getLBRules, setLBRules);
    fetchSection(cloudOps.getDNSZones, setDNSZones);
    fetchSection(cloudOps.getCertificates, setCerts);
    fetchSection(cloudOps.getCostCurrentMonth, setCost);
    fetchSection(cloudOps.getComplianceSummary, setCompliance);
    fetchSection(cloudOps.getComputeSizeAdvisory, setComputeSize);
    loadDeploy();
    setLastRefresh(new Date());
  }, [fetchSection, loadDeploy]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Cleanup poll on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Re-detect provider
  const handleRedetect = useCallback(async () => {
    setDetecting(true);
    const result = await cloudOps.forceDetect(authFetch);
    if (!cloudOps.isCloudOpsError(result)) {
      setProvider({ data: result, loading: false, error: null });
    }
    setDetecting(false);
    loadAll();
  }, [authFetch, loadAll]);

  // Show deploy confirmation dialog
  const handleDeployClick = useCallback(() => {
    setDeployConfirmOpen(true);
  }, []);

  // Execute deploy after confirmation
  const handleDeploy = useCallback(async () => {
    setDeployConfirmOpen(false);
    setDeploying(true);
    setDeployError(null);
    const result = await cloudOps.triggerDeploy(authFetch);
    if (cloudOps.isCloudOpsError(result)) {
      setDeployError(result.message);
      setDeploying(false);
      return;
    }
    // Start polling status after 3s delay (let GitHub create the run)
    setTimeout(() => {
      pollRef.current = setInterval(async () => {
        const statusRes = await cloudOps.getDeployStatus(authFetch);
        if (!cloudOps.isCloudOpsError(statusRes)) {
          const s = statusRes as DeployStatus | null;
          setDeployStatus(s);
          if (s && s.status === 'completed') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setDeploying(false);
            loadDeploy();
          }
        }
      }, 5000);
    }, 3000);
  }, [authFetch, deployHealth, loadDeploy]);

  const isLocal = provider.data?.provider === 'local';
  const isEnvDetection = provider.data?.detection_method === 'env' && provider.data?.provider === 'aws';

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1><CloudIcon size={24} /> Cloud Operations</h1>
          <p className="page-description">
            {provider.data
              ? `Provider: ${provider.data.provider.toUpperCase()} (${provider.data.region}) \u2022 Detection: ${provider.data.detection_method} \u2022 ${health.data?.status || 'loading...'}`
              : 'Loading cloud provider information...'}
          </p>
        </div>
        <div className="page-header-actions">
          <span className="last-refresh" style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            className="btn btn-secondary"
            onClick={handleRedetect}
            disabled={detecting}
            style={{ marginRight: '8px' }}
          >
            Re-detect Provider
          </button>
          <button
            className="btn btn-secondary"
            onClick={loadAll}
          >
            <RefreshIcon size={16} /> Refresh All
          </button>
        </div>
      </div>

      {/* IMDS Detection Banner */}
      {isEnvDetection && (
        <div
          data-testid="imds-banner"
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            backgroundColor: 'rgba(234, 179, 8, 0.1)',
            border: '1px solid rgba(234, 179, 8, 0.3)',
            color: '#eab308',
            fontSize: '13px',
          }}
        >
          <strong>Limited cloud data</strong> — IMDS metadata service not reachable from container.
          Compute health, IP allocations, DNS zones, certificates, and cost data require IMDS access.
          <br />
          <span style={{ color: '#9ca3af' }}>
            Fix: Set IMDSv2 hop limit to 2 in EC2 console (Instance &rarr; Actions &rarr; Modify instance metadata options),
            then restart the platform-api container.
          </span>
        </div>
      )}

      {/* Section 0: Deployment */}
      <div className="glass-panel" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ActivityIcon size={18} /> Deployment
        </h3>
        {deployLoading ? <SectionLoading /> : (
          <div>
            {/* PAT not configured banner */}
            {deployHealth && !deployHealth.pat_configured && (
              <div
                data-testid="deploy-pat-banner"
                style={{
                  padding: '12px 16px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  backgroundColor: 'var(--info-bg, rgba(59,130,246,0.1))',
                  border: '1px solid var(--info-border, rgba(59,130,246,0.3))',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                }}
              >
                <AlertIcon size={16} /> Deployment requires a GitHub Personal Access Token.
                Set <code>GITHUB_PAT</code> environment variable on the platform-api container.
              </div>
            )}

            {deployError && (
              <div className="alert alert-error" style={{ marginBottom: '12px', fontSize: '13px' }}>
                {deployError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {/* Current Status card */}
              <KPICard title="Current Status" icon={<CheckCircleIcon size={16} />}>
                <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
                  <div><strong>Deployed:</strong> v{deployHealth?.deployed_version || 'unknown'}</div>
                  <div><strong>Latest:</strong> v{deployHealth?.latest_version || 'unknown'}</div>
                  <div>
                    <strong>Status:</strong>{' '}
                    {deployHealth?.up_to_date
                      ? <StatusBadge status="Up to date" />
                      : <span style={{
                          padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                          backgroundColor: 'rgba(234,179,8,0.15)', color: '#eab308',
                          border: '1px solid rgba(234,179,8,0.3)',
                        }}>Update available</span>
                    }
                  </div>
                  <div><strong>GitHub PAT:</strong> {deployHealth?.pat_configured ? <StatusBadge status="ok" /> : <StatusBadge status="missing" />}</div>
                  <div><strong>GitHub API:</strong> {deployHealth?.github_reachable ? <StatusBadge status="ok" /> : <StatusBadge status="unreachable" />}</div>
                </div>
              </KPICard>

              {/* Deploy to Production card */}
              <KPICard title="Deploy to Production" icon={<CloudIcon size={16} />}>
                <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleDeployClick}
                    disabled={!deployHealth?.pat_configured || deploying || (deployStatus?.status === 'in_progress')}
                    style={{ marginBottom: '8px' }}
                  >
                    {deploying || deployStatus?.status === 'in_progress' ? 'Deploying...' : 'Deploy Now'}
                  </button>
                  {deployStatus && (
                    <>
                      <div><strong>Last deploy:</strong> {deployStatus.created_at ? timeAgo(deployStatus.created_at) : 'N/A'}</div>
                      <div><strong>Result:</strong> <StatusBadge status={deployStatus.conclusion || deployStatus.status} /></div>
                      <div><strong>SHA:</strong> <code>{deployStatus.head_sha}</code></div>
                      {deployStatus.html_url && (
                        <div>
                          <a href={deployStatus.html_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary, #3b82f6)', fontSize: '12px' }}>
                            View on GitHub &rarr;
                          </a>
                        </div>
                      )}
                    </>
                  )}
                  {!deployStatus && <div style={{ color: 'var(--text-secondary)' }}>No deployments yet</div>}
                </div>
              </KPICard>
            </div>

            {/* Recent deployments table */}
            <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Recent Deployments</h4>
            {deployHistory.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>No deployments yet</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px' }}>SHA</th>
                    <th style={{ textAlign: 'left', padding: '8px 4px' }}>Link</th>
                  </tr>
                </thead>
                <tbody>
                  {deployHistory.map((run) => (
                    <tr key={run.id} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                      <td style={{ padding: '8px 4px' }}>{run.created_at ? timeAgo(run.created_at) : 'N/A'}</td>
                      <td style={{ padding: '8px 4px' }}><StatusBadge status={run.conclusion || run.status} /></td>
                      <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{run.head_sha}</td>
                      <td style={{ padding: '8px 4px' }}>
                        {run.html_url && (
                          <a href={run.html_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary, #3b82f6)' }}>View</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* WQ-338: the demos deploy lane, beside the platform Deployment panel */}
      <div style={{ marginBottom: '16px' }}>
        <DemoPublishCard title="IWT Demo Center" />
      </div>

      {/* Section 1: Provider & Compute Health */}
      <div className="glass-panel" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ServerIcon size={18} /> Provider &amp; Compute Health
        </h3>
        {(provider.loading && !provider.data) ? <SectionLoading /> : provider.error ? (
          <SectionError error={provider.error} onRetry={() => {
            fetchSection(cloudOps.getProviderInfo, setProvider);
            fetchSection(cloudOps.getCloudHealth, setHealth);
            fetchSection(cloudOps.getComputeHealth, setCompute);
          }} />
        ) : (
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <KPICard title="Provider" icon={<CloudIcon size={16} />}>
              <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
                <div><strong>Name:</strong> {provider.data?.provider?.toUpperCase()}</div>
                <div><strong>Region:</strong> {provider.data?.region}</div>
                <div><strong>Zone:</strong> {provider.data?.zone || 'N/A'}</div>
                <div><strong>Account:</strong> {provider.data?.account_id || 'N/A'}</div>
                <div><strong>Detection:</strong> {provider.data?.detection_method}</div>
                <div><strong>OS:</strong> {provider.data?.os}</div>
              </div>
            </KPICard>

            <KPICard title="Compute" icon={<ServerIcon size={16} />}>
              {compute.loading ? <SectionLoading /> : compute.error ? (
                <InfoBanner message={compute.error} />
              ) : isEnvDetection ? (
                <div style={{ fontSize: '13px', lineHeight: 1.8, color: 'var(--text-secondary)' }}>
                  <div><strong>State:</strong> <span style={{ color: '#eab308' }}>Requires IMDS</span></div>
                  <div><strong>Instance:</strong> <span style={{ color: '#eab308' }}>Requires IMDS</span></div>
                  <div><strong>System:</strong> <span style={{ color: '#eab308' }}>Requires IMDS</span></div>
                  <div style={{ marginTop: '4px', fontSize: '12px' }}>IMDS not reachable — compute data unavailable</div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', lineHeight: 1.8 }}>
                  <div><strong>State:</strong> <StatusBadge status={compute.data?.instance_state || 'unknown'} /></div>
                  <div><strong>Instance:</strong> <StatusBadge status={compute.data?.instance_status || 'unknown'} /></div>
                  <div><strong>System:</strong> <StatusBadge status={compute.data?.system_status || 'unknown'} /></div>
                  {compute.data?.instance_id && <div><strong>ID:</strong> {compute.data.instance_id}</div>}
                  {compute.data?.note && <div style={{ marginTop: '4px', color: 'var(--text-secondary)', fontSize: '12px' }}>{compute.data.note}</div>}
                </div>
              )}
            </KPICard>

            <KPICard title="Available Services" icon={<ActivityIcon size={16} />}>
              {health.loading ? <SectionLoading /> : health.error ? (
                <InfoBanner message={health.error} />
              ) : (
                <div style={{ fontSize: '13px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <StatusBadge status={health.data?.status || 'unknown'} />
                    <span style={{ marginLeft: '8px' }}>{health.data?.available_services?.length || 0} services</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {health.data?.available_services?.map(svc => (
                      <span
                        key={svc}
                        style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          backgroundColor: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
                          border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
                        }}
                      >
                        {svc}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </KPICard>
          </div>
        )}
      </div>

      {/* Section 1b: Compute Size (SLM Serving) — WQ-332 / DM-28 */}
      <div className="glass-panel" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ServerIcon size={18} /> Compute Size (SLM Serving)
        </h3>
        {computeSize.loading ? <SectionLoading /> : computeSize.error ? (
          <SectionError error={computeSize.error} onRetry={() => fetchSection(cloudOps.getComputeSizeAdvisory, setComputeSize)} />
        ) : computeSize.data ? (
          <ComputeSizePanel data={computeSize.data} />
        ) : (
          <InfoBanner message="Compute size advisory unavailable" />
        )}
      </div>

      {/* Section 2: IP Allocations & Load Balancer */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div className="glass-panel" style={{ flex: '1 1 48%', minWidth: '400px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GlobeIcon size={18} /> IP Allocations
          </h3>
          {ips.loading ? <SectionLoading /> : ips.error ? (
            <SectionError error={ips.error} onRetry={() => fetchSection(cloudOps.getIPAllocations, setIPs)} />
          ) : !ips.data || (Array.isArray(ips.data) && ips.data.length === 0) || (!Array.isArray(ips.data) && (ips.data as unknown as Record<string, unknown>)?.error) ? (
            <InfoBanner message={(!Array.isArray(ips.data) && (ips.data as unknown as Record<string, unknown>)?.note as string) || (isLocal ? 'IP allocations simulated in local dev' : 'No IP allocations found')} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>IP</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Type</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Label</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Provider ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(ips.data) ? ips.data : []).map((ip, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{ip.ip_address}</td>
                    <td style={{ padding: '8px 4px' }}>{ip.allocation_type}</td>
                    <td style={{ padding: '8px 4px' }}><StatusBadge status={ip.status} /></td>
                    <td style={{ padding: '8px 4px' }}>{ip.label}</td>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontSize: '11px' }}>{ip.provider_allocation_id}</td>
                    <td style={{ padding: '8px 4px' }}>{ip.created_at ? new Date(ip.created_at).toLocaleDateString() : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="glass-panel" style={{ flex: '1 1 48%', minWidth: '400px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatabaseIcon size={18} /> LB Routing Rules
          </h3>
          {lbRules.loading ? <SectionLoading /> : lbRules.error ? (
            <SectionError error={lbRules.error} onRetry={() => fetchSection(cloudOps.getLBRules, setLBRules)} />
          ) : !lbRules.data || (Array.isArray(lbRules.data) && lbRules.data.length === 0) ? (
            <InfoBanner message={isLocal ? 'Load balancer simulated in local dev' : 'No LB rules configured'} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Rule ID</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Config File</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(lbRules.data) ? lbRules.data : []).map((rule, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{rule.rule_id}</td>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontSize: '11px' }}>{rule.config_file}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 3: DNS & Certificates */}
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <div className="glass-panel" style={{ flex: '1 1 48%', minWidth: '400px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GlobeIcon size={18} /> DNS Zones
          </h3>
          {dnsZones.loading ? <SectionLoading /> : dnsZones.error ? (
            <SectionError error={dnsZones.error} onRetry={() => fetchSection(cloudOps.getDNSZones, setDNSZones)} />
          ) : !dnsZones.data || (Array.isArray(dnsZones.data) && dnsZones.data.length === 0) ? (
            <InfoBanner message={isLocal ? 'DNS zones not available in local dev' : 'No DNS zones found'} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Zone Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Provider</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Records</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(dnsZones.data) ? dnsZones.data : []).map((zone, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{zone.zone_name}</td>
                    <td style={{ padding: '8px 4px' }}>{zone.provider}</td>
                    <td style={{ padding: '8px 4px' }}>{zone.record_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="glass-panel" style={{ flex: '1 1 48%', minWidth: '400px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldIcon size={18} /> TLS Certificates
          </h3>
          {certs.loading ? <SectionLoading /> : certs.error ? (
            <SectionError error={certs.error} onRetry={() => fetchSection(cloudOps.getCertificates, setCerts)} />
          ) : !certs.data || (Array.isArray(certs.data) && certs.data.length === 0) ? (
            <InfoBanner message={isLocal ? 'Certificates not available in local dev' : 'No certificates found'} />
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Domain</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Expires</th>
                  <th style={{ textAlign: 'left', padding: '8px 4px' }}>Provider ID</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(certs.data) ? certs.data : []).map((cert, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace' }}>{cert.domain}</td>
                    <td style={{ padding: '8px 4px' }}><StatusBadge status={cert.status} /></td>
                    <td style={{ padding: '8px 4px' }}>{cert.expires ? new Date(cert.expires).toLocaleDateString() : 'N/A'}</td>
                    <td style={{ padding: '8px 4px', fontFamily: 'monospace', fontSize: '11px' }}>{cert.provider_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Section 4: Cost Summary */}
      <div className="glass-panel" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ActivityIcon size={18} /> Cost Summary (Current Month)
        </h3>
        {cost.loading ? <SectionLoading /> : cost.error ? (
          <SectionError error={cost.error} onRetry={() => fetchSection(cloudOps.getCostCurrentMonth, setCost)} />
        ) : (
          <div>
            {cost.data?.error ? (
              <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <AlertIcon size={16} />
                <span>Cost Explorer degraded: {cost.data.error}</span>
              </div>
            ) : null}
            <div style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px' }}>
              Month-to-Date: ${(cost.data?.total_usd ?? 0).toFixed(2)}
              {isLocal && (
                <span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '12px' }}>
                  local development
                </span>
              )}
            </div>
            {cost.data?.by_service && cost.data.by_service.length > 0 && (
              <table style={{ width: '100%', maxWidth: '500px', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                    <th style={{ textAlign: 'left', padding: '8px 4px' }}>Service</th>
                    <th style={{ textAlign: 'right', padding: '8px 4px' }}>Amount (USD)</th>
                  </tr>
                </thead>
                <tbody>
                  {cost.data.by_service.map((svc, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                      <td style={{ padding: '8px 4px' }}>{svc.service}</td>
                      <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>${(svc.amount_usd ?? 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {cost.data?.note && (
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '12px' }}>
                {cost.data.note}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Section 5: Compliance Posture */}
      <div className="glass-panel" style={{ padding: '20px', marginBottom: '16px' }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShieldIcon size={18} /> Compliance Posture
        </h3>
        {compliance.loading ? <SectionLoading /> : compliance.error ? (
          <SectionError error={compliance.error} onRetry={() => fetchSection(cloudOps.getComplianceSummary, setCompliance)} />
        ) : isLocal && (!compliance.data?.inherited_frameworks || compliance.data.inherited_frameworks.length === 0) ? (
          <InfoBanner message="No compliance data \u2014 local development" />
        ) : (
          <div>
            {/* Inherited frameworks badges */}
            {compliance.data?.inherited_frameworks && compliance.data.inherited_frameworks.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Inherited Frameworks</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {compliance.data.inherited_frameworks.map((fw, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        backgroundColor: 'rgba(34, 197, 94, 0.15)',
                        border: '1px solid rgba(34, 197, 94, 0.4)',
                        color: '#22c55e',
                      }}
                    >
                      <CheckCircleIcon size={12} /> {fw.framework}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Partially inherited */}
            {compliance.data?.partially_inherited && compliance.data.partially_inherited.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Partially Inherited</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {compliance.data.partially_inherited.map((fw, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 500,
                        backgroundColor: 'rgba(107, 114, 128, 0.15)',
                        border: '1px solid rgba(107, 114, 128, 0.4)',
                        color: '#9ca3af',
                      }}
                    >
                      {fw.framework} (partial)
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Platform must prove */}
            {compliance.data?.platform_must_prove && compliance.data.platform_must_prove.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Platform Must Independently Prove</h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: 1.8 }}>
                  {compliance.data.platform_must_prove.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Controls detail */}
            {compliance.data?.controls && compliance.data.controls.length > 0 && (
              <div>
                <h4 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Control Details</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.1))' }}>
                      <th style={{ textAlign: 'left', padding: '6px 4px' }}>Framework</th>
                      <th style={{ textAlign: 'left', padding: '6px 4px' }}>Domain</th>
                      <th style={{ textAlign: 'left', padding: '6px 4px' }}>Scope</th>
                      <th style={{ textAlign: 'left', padding: '6px 4px' }}>Not Covered</th>
                      <th style={{ textAlign: 'left', padding: '6px 4px' }}>Attestation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compliance.data.controls.map((ctrl, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-color, rgba(255,255,255,0.05))' }}>
                        <td style={{ padding: '6px 4px' }}>{ctrl.framework}</td>
                        <td style={{ padding: '6px 4px' }}>{ctrl.control_domain}</td>
                        <td style={{ padding: '6px 4px' }}>{ctrl.scope}</td>
                        <td style={{ padding: '6px 4px' }}>{ctrl.not_covered}</td>
                        <td style={{ padding: '6px 4px' }}>
                          {ctrl.attestation_url ? (
                            <a href={ctrl.attestation_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary, #3b82f6)' }}>View</a>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Deploy confirmation dialog */}
      {deployConfirmOpen && (
        <div
          onClick={() => setDeployConfirmOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--glass-bg)',
              backdropFilter: 'var(--glass-blur-lg)',
              WebkitBackdropFilter: 'var(--glass-blur-lg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-2xl, 16px)',
              boxShadow: 'var(--glass-shadow-lg)',
              width: '100%', maxWidth: '420px',
            }}
          >
            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid var(--border-secondary, rgba(255,255,255,0.08))' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)' }}>
                Deploy to Production
              </h3>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                Deploy v{deployHealth?.deployed_version || 'current version'} to production? This will trigger a deployment pipeline.
              </p>
            </div>
            <div style={{ padding: '16px 24px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button className="btn btn-secondary" onClick={() => setDeployConfirmOpen(false)} style={{ minWidth: '80px' }}>
                Cancel
              </button>
              <button className="btn btn-warning" onClick={handleDeploy} style={{ minWidth: '100px' }}>
                Deploy Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudOpsPage;
